import { useState, useEffect } from 'react';
import { getMonitorStatus, startMonitoring, stopMonitoring, updateMonitorInterval } from '../utils/api';

interface MonitorControlPanelProps {
  className?: string;
  onStatusChange?: () => void;
  onOpenSettings?: () => void;
}

/**
 * 监控控制面板组件
 * 用于控制监控的启动、停止和设置监控间隔
 */
const MonitorControlPanel: React.FC<MonitorControlPanelProps> = ({ 
  className = '', 
  onStatusChange,
  onOpenSettings
}) => {
  const [status, setStatus] = useState<{
    isMonitoring: boolean;
    intervalMinutes: number;
    cronPattern?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [operating, setOperating] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState('60');

  // 可选的监控间隔（分钟）
  const intervalOptions = [
    { value: '5', label: '5分钟', cron: '*/5 * * * *' },
    { value: '10', label: '10分钟', cron: '*/10 * * * *' },
    { value: '15', label: '15分钟', cron: '*/15 * * * *' },
    { value: '30', label: '30分钟', cron: '*/30 * * * *' },
    { value: '60', label: '1小时', cron: '0 */1 * * *' },
    { value: '120', label: '2小时', cron: '0 */2 * * *' },
    { value: '240', label: '4小时', cron: '0 */4 * * *' },
    { value: '360', label: '6小时', cron: '0 */6 * * *' },
  ];

  // 加载监控状态
  const loadStatus = async () => {
    try {
      setLoading(true);
      const statusData = await getMonitorStatus();
      setStatus(statusData);
      setSelectedInterval(statusData.intervalMinutes.toString());
    } catch (error) {
      console.error('获取监控状态失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 启动监控
  const handleStartMonitoring = async () => {
    try {
      setOperating(true);
      const selectedOption = intervalOptions.find(opt => opt.value === selectedInterval);
      await startMonitoring(selectedOption?.cron || '0 */1 * * *');
      await loadStatus();
      onStatusChange?.();
    } catch (error) {
      console.error('启动监控失败:', error);
    } finally {
      setOperating(false);
    }
  };

  // 停止监控
  const handleStopMonitoring = async () => {
    try {
      setOperating(true);
      await stopMonitoring();
      await loadStatus();
      onStatusChange?.();
    } catch (error) {
      console.error('停止监控失败:', error);
    } finally {
      setOperating(false);
    }
  };

  // 更新监控间隔
  const handleUpdateInterval = async () => {
    try {
      setOperating(true);
      const selectedOption = intervalOptions.find(opt => opt.value === selectedInterval);
      if (selectedOption) {
        await updateMonitorInterval(selectedOption.cron);
        await loadStatus();
        onStatusChange?.();
      }
    } catch (error) {
      console.error('更新监控间隔失败:', error);
    } finally {
      setOperating(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  if (loading) {
    return (
      <div className={`card bg-base-100 shadow-lg ${className}`}>
        <div className="card-body">
          <h3 className="card-title text-lg">
            <span className="text-primary">🎛️</span>
            监控控制面板
          </h3>
          <div className="flex items-center justify-center py-4">
            <span className="loading loading-spinner loading-md text-primary"></span>
            <span className="ml-3 text-base-content/60">加载中...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`card bg-base-100 shadow-lg ${className}`}>
      <div className="card-body">
        <div className="flex justify-between items-center">
          <h3 className="card-title text-lg">
            <span className="text-primary">🎛️</span>
            监控控制面板
          </h3>
          {onOpenSettings && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={onOpenSettings}
              title="高级设置"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          )}
        </div>
        
        <div className="space-y-4">
          {/* 状态和间隔设置 - 横向布局 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* 当前状态显示 */}
            <div className={`alert ${status?.isMonitoring ? 'bg-green-500 text-white' : 'alert-info'}`}>
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <div className="flex items-center space-x-2">
                    <span className={`w-3 h-3 rounded-full ${
                      status?.isMonitoring ? 'bg-white animate-pulse' : 'bg-gray-400'
                    }`}></span>
                    <span className="font-medium">
                      监控状态: {status?.isMonitoring ? '运行中' : '已停止'}
                    </span>
                  </div>
                  {status?.isMonitoring && (
                    <div className="flex items-center space-x-2">
                      <span className="w-3 h-3"></span>
                      <span className="text-sm">
                        当前间隔: {status.intervalMinutes}分钟
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 监控间隔设置 */}
            <div>
              <label className="label">
                <span className="label-text font-medium">监控间隔设置</span>
              </label>
              <select 
                className="select select-bordered w-full"
                value={selectedInterval}
                onChange={(e) => setSelectedInterval(e.target.value)}
                disabled={operating}
              >
                {intervalOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 控制按钮 */}
          <div className="flex gap-3">
            {!status?.isMonitoring ? (
              <button
                className={`btn btn-success flex-1 ${operating ? 'loading' : ''}`}
                onClick={handleStartMonitoring}
                disabled={operating}
              >
                {operating ? '' : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m6-6v8a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2h8a2 2 0 012 2z" />
                    </svg>
                    启动监控
                  </>
                )}
              </button>
            ) : (
              <button
                className={`btn btn-error flex-1 ${operating ? 'loading' : ''}`}
                onClick={handleStopMonitoring}
                disabled={operating}
              >
                {operating ? '' : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10h6v4H9z" />
                    </svg>
                    停止监控
                  </>
                )}
              </button>
            )}

            {status?.isMonitoring && (
              <button
                className={`btn btn-warning ${operating ? 'loading' : ''}`}
                onClick={handleUpdateInterval}
                disabled={operating || selectedInterval === status.intervalMinutes.toString()}
              >
                {operating ? '' : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    更新间隔
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitorControlPanel; 